import { AutocompleteValue, Autocomplete as MuiAutocomplete, AutocompleteProps as MuiAutocompleteProps } from "@mui/material";
import { ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { TextField } from "./TextField";

export type AutoCompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined = undefined,
> = MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>;

export const Autocomplete = <
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  ...props
}: AutoCompleteProps<T, Multiple, DisableClearable, FreeSolo>) => {
  return <MuiAutocomplete {...props} />;
};

export interface FormAutoCompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined = undefined,
> extends Omit<MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, "renderInput"> {
  renderInput?: MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>["renderInput"];
  label?: ReactNode;
  placeholder?: string;
  name: string;
}

export default function FormAutoComplete<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({ name, label, placeholder, disabled, ...props }: FormAutoCompleteProps<T, Multiple, DisableClearable, FreeSolo>) {
  const { control, trigger } = useFormContext();

  return (
    <>
      <Controller
        control={control}
        name={name}
        render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => (
          <Autocomplete
            disabled={disabled}
            onBlur={onBlur}
            value={value as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo> | undefined}
            onChange={(_, value) => {
              onChange(value);
              void trigger(name);
            }}
            onInputChange={(_, value) => {
              onChange(value);
              void trigger(name);
            }}
            {...props}
            renderInput={(params) => (
              <TextField
                {...params}
                error={!disabled && !!error}
                helperText={!disabled && error ? error.message : " "}
                label={label}
                placeholder={placeholder}
              />
            )}
          />
        )}
      />
    </>
  );
}
